import { Button, Card, CardContent, CardHeader, CardTitle, ScrollArea, Search, Spinner, TreeList } from '@ibsys/kit';
import { DragDropProvider, DragOverlay, type DragEndEvent, PointerSensor } from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { isSortable } from '@dnd-kit/react/sortable';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { GripVerticalIcon, FolderIcon } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCompetencyTree,
  useCreateCompetency,
  useReorderCompetencies,
  coachingKeys,
  type CoachingTreeResponse,
  type ReorderItem,
} from '@/entities/coaching';
import { CreateCompetencyDialog } from '../CreateCompetencyDialog/CreateCompetencyDialog';
import { RootNode } from './RootNode';

type CompetencyTreeProps = {
  selectedCompetencyId?: number;
  onSelectCompetency: (id: number) => void;
  isDesktop?: boolean;
};

export const CompetencyTree: FC<CompetencyTreeProps> = ({ selectedCompetencyId, onSelectCompetency, isDesktop }) => {
  const { t } = useTranslation(['common', 'coaching']);
  const queryClient = useQueryClient();
  const { data: treeData, isLoading } = useCompetencyTree();
  const createMutation = useCreateCompetency();
  const reorderMutation = useReorderCompetencies();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);

  const competencies = useMemo(() => {
    if (!treeData?.competencies) return [];
    return treeData.competencies.filter((c) => !c.isDeleted).sort((a, b) => a.orderPosition - b.orderPosition);
  }, [treeData?.competencies]);

  // Автоматически раскрываем узел при навигации через URL
  useEffect(() => {
    if (!selectedCompetencyId || !competencies.length) return;

    // Если выбран корневой узел — раскрываем его
    const isRoot = competencies.some((c) => c.id === selectedCompetencyId);
    if (isRoot) {
      const id = String(selectedCompetencyId);
      setExpandedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      return;
    }

    // Если выбран дочерний узел — раскрываем его родителя
    const parentRoot = competencies.find((root) =>
      root.children.some((c) => !c.isDeleted && c.id === selectedCompetencyId),
    );

    if (parentRoot) {
      const id = String(parentRoot.id);
      setExpandedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  }, [selectedCompetencyId, competencies]);

  // Фильтрация по поисковому запросу
  const filteredCompetencies = useMemo(() => {
    if (!searchTerm.trim()) return competencies;

    const term = searchTerm.toLowerCase();

    return competencies.filter((root) => {
      const rootMatch = root.name.toLowerCase().includes(term);
      const childMatch = root.children.some((c) => !c.isDeleted && c.name.toLowerCase().includes(term));
      return rootMatch || childMatch;
    });
  }, [competencies, searchTerm]);

  // Создание корневой компетенции
  const handleCreateRoot = () => {
    setCreateParentId(null);
    setIsCreateDialogOpen(true);
  };

  // Создание дочерней компетенции
  const handleAddChild = (parentId: number) => {
    setCreateParentId(parentId);
    setIsCreateDialogOpen(true);
  };

  const handleCreateSubmit = (name: string) => {
    const siblings = createParentId
      ? (competencies.find((c) => c.id === createParentId)?.children.filter((c) => !c.isDeleted) ?? [])
      : competencies;

    createMutation.mutate(
      {
        name,
        parentCompetencyId: createParentId,
        orderPosition: siblings.length,
      },
      {
        onSuccess: (created) => {
          setIsCreateDialogOpen(false);
          if (createParentId) {
            const id = String(createParentId);
            setExpandedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
          }
          onSelectCompetency(created.id);
        },
      },
    );
  };

  // Переупорядочивание дочерних компетенций внутри родителя
  const handleChildReorder = (_parentId: number, items: ReorderItem[]) => {
    // Оптимистичное обновление
    const prevData = queryClient.getQueryData<CoachingTreeResponse>(coachingKeys.tree());
    if (prevData) {
      const updated: CoachingTreeResponse = {
        competencies: prevData.competencies.map((root) => ({
          ...root,
          children: root.children.map((child) => {
            const item = items.find((i) => i.id === child.id);
            return item ? { ...child, orderPosition: item.orderPosition } : child;
          }),
        })),
      };
      queryClient.setQueryData(coachingKeys.tree(), updated);
    }

    reorderMutation.mutate({ items });
  };

  // Drag-drop для корневых компетенций
  const handleRootDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return;
    const { source } = event.operation;
    if (!isSortable(source)) return;
    const { initialIndex, index } = source;
    if (initialIndex === index) return;

    const reordered = [...filteredCompetencies];
    const [moved] = reordered.splice(initialIndex, 1);
    reordered.splice(index, 0, moved);

    const items: ReorderItem[] = reordered.map((c, idx) => ({
      id: c.id,
      parentCompetencyId: null,
      orderPosition: idx,
    }));

    // Оптимистичное обновление
    const prevData = queryClient.getQueryData<CoachingTreeResponse>(coachingKeys.tree());
    if (prevData) {
      const updated: CoachingTreeResponse = {
        competencies: prevData.competencies.map((root) => {
          const item = items.find((i) => i.id === root.id);
          return item ? { ...root, orderPosition: item.orderPosition } : root;
        }),
      };
      queryClient.setQueryData(coachingKeys.tree(), updated);
    }

    reorderMutation.mutate({ items });
  };

  return (
    <>
      <Card className="flex h-full w-full flex-col gap-0 overflow-hidden pb-0">
        <CardHeader className="flex h-auto flex-col gap-4 border-b border-border bg-card p-4">
          <div className="flex w-full items-center gap-4">
            <CardTitle className="text-base font-bold">{t('coaching:title')}</CardTitle>
            <Button variant="default" size="sm" className="ml-auto" onClick={handleCreateRoot}>
              {t('common:add')}
            </Button>
          </div>

          <Search
            placeholder={t('coaching:search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </CardHeader>

        <CardContent className="h-full overflow-hidden p-0">
          <ScrollArea className="h-full">
            <div className="p-2">
              {isLoading && (
                <div className="flex items-center justify-center p-8">
                  <Spinner className="size-6" />
                </div>
              )}

              {!isLoading && filteredCompetencies.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">{t('coaching:noCompetencies')}</div>
              )}

              {!isLoading && filteredCompetencies.length > 0 && (
                <DragDropProvider
                  sensors={[
                    PointerSensor.configure({
                      activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })],
                    }),
                  ]}
                  modifiers={[RestrictToVerticalAxis]}
                  onDragEnd={handleRootDragEnd}
                >
                  <TreeList.Root
                    value={expandedIds}
                    onValueChange={setExpandedIds}
                    activeNodeId={selectedCompetencyId ? String(selectedCompetencyId) : undefined}
                  >
                    {filteredCompetencies.map((competency, index) => (
                      <RootNode
                        key={competency.id}
                        index={index}
                        competency={competency}
                        onSelect={onSelectCompetency}
                        onAddChild={handleAddChild}
                        onReorder={handleChildReorder}
                        isDesktop={isDesktop}
                      />
                    ))}
                  </TreeList.Root>
                  <DragOverlay>
                    {(source) => {
                      const root = filteredCompetencies.find((c) => `root-competency-${c.id}` === String(source.id));
                      if (!root) return null;
                      return (
                        <div className="flex items-center gap-2 rounded-lg bg-card px-3 py-2 shadow-lg ring-1 ring-border">
                          <GripVerticalIcon size={14} className="shrink-0 text-muted-foreground" />
                          <FolderIcon size={14} className="shrink-0 text-muted-foreground" />
                          <span className="text-sm font-bold">{root.name}</span>
                        </div>
                      );
                    }}
                  </DragOverlay>
                </DragDropProvider>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <CreateCompetencyDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
        isPending={createMutation.isPending}
      />
    </>
  );
};
